import { modifier } from 'ember-modifier'

interface Signature {
  Args: {
    Named: Record<string, unknown>
    Positional: [() => unknown]
  }
  Element: HTMLElement
}

/**
 * A modifier that waits for all images to load before calling the callback.
 *
 * Ideally, the window 'load' event would serve this purpose, but it is called too early in the page lifecycle of an
 * ember application.
 */
export default modifier<Signature>(function onImagesLoaded(element, positional, _named) {
  const [onLoad] = positional

  // Identify all images in element that are not yet loaded
  const imagesToLoad = Array.from(element.querySelectorAll('img')).filter((imageElement) => {
    // images that dont yet have an src attribute should also be waited for
    return !imageElement.complete || !imageElement.getAttribute('src')
  })
  let remainingImagesToLoad = imagesToLoad.length

  // If everything's already loaded, call the callback
  if (remainingImagesToLoad === 0) {
    onLoad()
    return
  }

  /* After all those images are loaded, call the callback.
   * For each load event, we could iterate over the 'complete' status of each image, but since there could be many
   * hundreds of images, it seems sensible / more performant to just keep track of it with a counter.
   */
  const loadListener = () => {
    remainingImagesToLoad -= 1
    if (remainingImagesToLoad === 0) onLoad()
  }

  imagesToLoad.forEach((imageElement) => imageElement.addEventListener('load', loadListener))
  return () => {
    imagesToLoad.forEach((imageElement) => imageElement.removeEventListener('load', loadListener))
  }
})
